import React from 'react';
import { Link } from 'react-router-dom';
import type { AdminDemoLeadConversionInput } from '../legalTypes';
import { useDemoLeadRouteQuery } from '../hooks/useLegalRouteQueries';
import { useSuperAdminStore } from '../store/useSuperAdminStore';
import { AdminButton } from './components';
import DemoLeadConversionWizard from './legal/DemoLeadConversionWizard';
import DemoLeadDetailDrawer from './legal/DemoLeadDetailDrawer';
import DemoLeadTable from './legal/DemoLeadTable';
import LegalPagination from './legal/LegalPagination';
import './styles/adminLegal.css';

const useDemoLeadStore = () => ({
  page: useSuperAdminStore((state) => state.demoLeads),
  detail: useSuperAdminStore((state) => state.demoLeadDetail),
  conversion: useSuperAdminStore((state) => state.demoLeadConversion),
  conflict: useSuperAdminStore((state) => state.demoLeadConversionConflict),
  loading: useSuperAdminStore((state) => state.demoLeadLoading),
  detailLoading: useSuperAdminStore((state) => state.demoLeadDetailLoading),
  mutating: useSuperAdminStore((state) => state.demoLeadMutating),
  converting: useSuperAdminStore((state) => state.demoLeadConverting),
  error: useSuperAdminStore((state) => state.demoLeadError),
  fetch: useSuperAdminStore((state) => state.fetchDemoLeads),
  load: useSuperAdminStore((state) => state.loadDemoLead),
  clear: useSuperAdminStore((state) => state.clearDemoLead),
  updateStatus: useSuperAdminStore((state) => state.updateDemoLeadStatus),
  convert: useSuperAdminStore((state) => state.convertDemoLead),
  clearConversion: useSuperAdminStore((state) => state.clearDemoLeadConversion),
});

interface DemoLeadFiltersProps {
  search: string;
  status: string;
  kind: string;
  onSearch: (value: string) => void;
  onStatus: (value: string) => void;
  onKind: (value: string) => void;
}

const DemoLeadFilters: React.FC<DemoLeadFiltersProps> = ({
  search,
  status,
  kind,
  onSearch,
  onStatus,
  onKind,
}) => (
  <div className="admin-legal-filters" role="search" aria-label="Demo lead filters">
    <label className="admin-legal-search">Search demo leads
      <input type="search" value={search} onChange={(event) => onSearch(event.target.value)} />
    </label>
    <label>Review state
      <select value={status} onChange={(event) => onStatus(event.target.value)}>
        <option value="">All states</option>
        <option value="new">New</option>
        <option value="reviewing">Reviewing</option>
        <option value="rejected">Rejected</option>
        <option value="converted">Converted</option>
      </select>
    </label>
    <label>Request kind
      <select value={kind} onChange={(event) => onKind(event.target.value)}>
        <option value="">All kinds</option>
        <option value="demo">Demo</option>
        <option value="consultation">Consultation</option>
      </select>
    </label>
  </div>
);

const ConversionOutcome: React.FC<{
  companyId: number;
  packageId: string;
  isReplay: boolean;
}> = ({ companyId, packageId, isReplay }) => (
  <div className="admin-legal-callout admin-legal-callout--success" role="status">
    <strong>{isReplay
      ? 'This lead was already converted by the same command.'
      : 'Customer and package queued.'}</strong>
    <p>No duplicate customer or package was created.</p>
    <Link to={`/super-admin/documents?company=${companyId}&package=${packageId}`}>
      Open document package
    </Link>
  </div>
);

const AdminDemoLeadsRoute: React.FC = () => {
  const query = useDemoLeadRouteQuery();
  const store = useDemoLeadStore();
  const fetchDemoLeads = store.fetch;
  const loadDemoLead = store.load;
  const clearDemoLead = store.clear;
  const [wizardOpen, setWizardOpen] = React.useState(false);
  React.useEffect(() => {
    void fetchDemoLeads(query.query).catch(() => undefined);
  }, [fetchDemoLeads, query.query]);
  React.useEffect(() => {
    if (query.selectedLeadId) {
      void loadDemoLead(query.selectedLeadId).catch(() => undefined);
    } else {
      clearDemoLead();
    }
  }, [clearDemoLead, loadDemoLead, query.selectedLeadId]);

  const closeDetail = () => {
    setWizardOpen(false);
    query.setSelectedLead(null);
    store.clear();
  };
  const openWizard = () => {
    store.clearConversion();
    setWizardOpen(true);
  };
  const closeWizard = () => setWizardOpen(false);
  const convertLead = async (input: AdminDemoLeadConversionInput) => {
    if (!store.detail) {
      throw new Error('Select a demo lead before converting it.');
    }
    const result = await store.convert(store.detail.id, input);
    query.setSelectedLead(null);
    store.clear();
    return result;
  };
  const emptyState = (
    <div role="status" aria-label="No demo leads">
      <strong>No demo leads match these filters.</strong>
      <span>Clear search or review-state filters to inspect another lead.</span>
    </div>
  );
  const showRouteError = store.error && store.page.items.length === 0 && !store.detailLoading;

  return (
    <section className="admin-legal-workspace" aria-labelledby="demo-leads-title">
      <header className="admin-legal-page-header">
        <div><span className="admin-legal-eyebrow">Private lead inbox</span><h2 id="demo-leads-title">Demo leads</h2><p>Review stored inquiries before explicitly creating customer and legal records.</p></div>
      </header>
      <DemoLeadFilters search={query.searchText} status={query.status} kind={query.kind} onSearch={query.setSearchText} onStatus={query.setStatus} onKind={query.setKind} />
      {store.loading && <div role="status" aria-label="Loading demo leads" className="admin-legal-loading">Loading demo leads…</div>}
      {showRouteError && <div role="alert" aria-label="Demo leads unavailable" className="admin-legal-callout admin-legal-callout--danger"><strong>Demo leads are temporarily unavailable.</strong><p>{store.error?.message}</p><AdminButton onClick={() => void store.fetch(query.query).catch(() => undefined)}>Retry demo leads</AdminButton></div>}
      {!store.loading && !showRouteError && <DemoLeadTable leads={store.page.items} onOpen={(lead) => query.setSelectedLead(lead.id)} emptyState={emptyState} />}
      {!store.loading && !showRouteError && <LegalPagination label="Demo leads" page={store.page.page} pageSize={store.page.pageSize} totalCount={store.page.totalCount} onPage={query.setPage} />}
      {store.conversion && <ConversionOutcome {...store.conversion} />}
      <DemoLeadDetailDrawer lead={store.detail} loading={store.detailLoading} mutating={store.mutating} onClose={closeDetail} onConvert={openWizard} onStatus={(status) => store.detail && void store.updateStatus(store.detail.id, { status, expectedVersion: store.detail.version }).catch(() => undefined)} />
      {wizardOpen && store.detail && <DemoLeadConversionWizard open lead={store.detail} pending={store.converting} error={store.error} conflict={store.conflict} onConvert={convertLead} onClose={closeWizard} />}
    </section>
  );
};

export default AdminDemoLeadsRoute;
